import type { IconWeight } from '@phosphor-icons/react'
import { useEffect, useState } from 'react'
import DOMPurify from 'dompurify'

// SVG icon mapping - maps icon names to SVG files in /icons/
const svgIconMap = {
  'home-outline': 'f-home',
  'people-outline': 'f-team',
  'folder-outline': 'f-folder',
  'checkbox-outline': 'f-tasks',
  'ticket-outline': 'f-forms',
  'time-outline': 'f-clock',
  'call-outline': 'f-funnel',
  'person-outline': 'f-user',
  'bulb-outline': 'f-pipeline',
  'document-text-outline': 'f-document',
  'receipt-outline': 'f-billable',
  'chatbubble-outline': 'f-discuss',
  'grid-outline': 'f-inventory',
  'cube-outline': 'f-box',
  'megaphone-outline': 'f-megaphone',
  'settings-outline': 'f-settings',
  'log-out-outline': 'f-sign-out',
  'bell-outline': 'f-bell',
  'building-outline': 'f-entity',
  'organization-outline': 'f-organization',
  'deal-outline': 'f-deal',
  'person-circle-outline': 'f-profile',
  'today-outline': 'f-dashboard',
  'trending-up-outline': 'f-trending',
  'chart-bar-outline': 'f-analytics',
  'mail-outline': 'f-mail',
  'sticky-note-outline': 'f-note',
  'calendar-outline': 'f-calendar',
  'link-outline': 'f-workflow',
  'list-outline': 'f-list',
  'list-bullets-outline': 'f-list',
  'clipboard-text-outline': 'f-forms',
  'pulse-outline': 'f-realtime',
  'contract-outline': 'f-contract',
  'card-outline': 'f-card',
  'expense-outline': 'f-expense',
  'search-outline': 'f-search',
} as const

type IconName = keyof typeof svgIconMap

interface LocalIconProps {
  className?: string
  /**
   * Phosphor-compatible weight. `fill` (or `duotone`) renders the solid
   * selected-state glyph, `f-<name>-fill.svg`; everything else renders the
   * outline glyph, `f-<name>.svg`. Both are emitted by
   * `scripts/generate-nav-icons.mjs` from one shared silhouette.
   */
  weight?: IconWeight
}

const FILL_WEIGHTS: ReadonlySet<IconWeight> = new Set(['fill', 'duotone'])

export function localIconSrc(
  svgName: (typeof svgIconMap)[IconName],
  weight: IconWeight = 'regular',
) {
  return `/icons/${svgName}${FILL_WEIGHTS.has(weight) ? '-fill' : ''}.svg`
}

// In-memory cache for fetched SVG content
const svgCache = new Map<string, string>()
const svgPromises = new Map<string, Promise<string>>()

function fetchSvg(url: string): Promise<string> {
  const cached = svgCache.get(url)
  if (cached) return Promise.resolve(cached)

  const existing = svgPromises.get(url)
  if (existing) return existing

  const promise = fetch(url)
    .then(res => res.text())
    .then(text => {
      // Strip width/height attributes so the SVG scales via CSS className
      const stripped = text
        .replace(/\s*width="[^"]*"/, '')
        .replace(/\s*height="[^"]*"/, '')
      // Sanitize: blocks script tags, on* event handlers, javascript: hrefs, foreignObject, etc.
      const cleaned = DOMPurify.sanitize(stripped, {
        USE_PROFILES: { svg: true, svgFilters: true },
      })
      svgCache.set(url, cleaned)
      svgPromises.delete(url)
      return cleaned
    })
    .catch(() => {
      svgPromises.delete(url)
      return ''
    })

  svgPromises.set(url, promise)
  return promise
}

/**
 * Load an icon's SVG and hand it to `onLoad`, unless the returned cancel
 * function runs first. An icon that unmounts mid-fetch (or a test environment
 * torn down before the fetch lands) must never receive a late state update:
 * React reads `window` while scheduling it, which throws once the DOM is gone.
 */
export function loadInlineSvg(
  src: string,
  onLoad: (svg: string) => void,
): () => void {
  let active = true
  void fetchSvg(src).then(svg => {
    if (active) onLoad(svg)
  })
  return () => {
    active = false
  }
}

/**
 * Inline SVG icon component.
 * Renders SVG content inline so it inherits `color` (via currentColor)
 * and `--icon-accent` CSS variable from its parent.
 */
function InlineSvgIcon({
  src,
  prefetch,
  className,
}: {
  src: string
  /** Sibling weight to warm the cache with, so a later swap is instant. */
  prefetch?: string
  className?: string
}) {
  // Keep the previous weight on screen while the next one loads: an active
  // row swapping outline → fill must never blink to an empty box.
  const [svgContent, setSvgContent] = useState(() => svgCache.get(src) ?? '')

  useEffect(() => {
    if (svgCache.has(src)) {
      setSvgContent(svgCache.get(src)!)
      return
    }
    return loadInlineSvg(src, setSvgContent)
  }, [src])

  useEffect(() => {
    if (prefetch && !svgCache.has(prefetch)) void fetchSvg(prefetch)
  }, [prefetch])

  if (!svgContent) {
    return <span className={className} style={{ display: 'inline-flex' }} />
  }

  return (
    <span
      className={`inline-svg-icon ${className ?? ''}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        lineHeight: 0,
      }}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  )
}

export function createLocalIcon(
  name: IconName,
  defaultWeight: IconWeight = 'regular',
) {
  const svgName = svgIconMap[name]

  return function LocalIcon({
    className,
    weight = defaultWeight,
  }: LocalIconProps) {
    const src = localIconSrc(svgName, weight)
    const sibling = localIconSrc(
      svgName,
      FILL_WEIGHTS.has(weight) ? 'regular' : 'fill',
    )
    return <InlineSvgIcon src={src} prefetch={sibling} className={className} />
  }
}
